import React, { ReactNode } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';

import { ChevronDownIcon } from 'src/shared/components/icons';
import { useTranslate } from 'src/shared/providers';

interface Props<T> {
  selectedOption?: T | null;
  options?: T[];
  optionValue?: (option: T) => string | number | undefined;
  optionLabel?: (option: T) => ReactNode;
  onOptionSelect?: (option: T) => void;
  optionsLoading?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  placeholder?: string;
  noOptionsText?: string;
  width?: string | number;
  height?: string | number;
  MenuProps?: SelectProps['MenuProps'];
  error?: boolean;
  warning?: boolean;
  sx?: SelectProps['sx'];
}

export const OptionSelect = <T,>({
  selectedOption,
  options,
  optionValue,
  optionLabel,
  onOptionSelect,
  optionsLoading,
  loading,
  disabled,
  onOpen,
  onClose,
  placeholder,
  noOptionsText,
  width = 120,
  height = 24,
  MenuProps,
  error,
  warning,
  sx,
}: Props<T>) => {
  const { t } = useTranslate();

  return (
    <Select
      value={selectedOption && optionValue ? optionValue(selectedOption) : ''}
      renderValue={selectedOption && optionLabel ? () => optionLabel(selectedOption) : undefined}
      displayEmpty={true}
      onOpen={onOpen}
      onClose={onClose}
      IconComponent={
        loading ? () => <CircularProgress style={{ width: 24, height: 'auto' }} sx={{ mr: 2 }} /> : ChevronDownIcon
      }
      disabled={disabled}
      MenuProps={MenuProps}
      sx={{
        fontSize: '0.875rem',
        color: (theme) => (!selectedOption ? theme.palette.text.disabled : undefined),
        width: width,
        '& .MuiSelect-select': {
          height: height,
          py: 0,
          lineHeight: typeof height === 'number' ? `${height}px` : height,
          '~ svg': {
            top: 'calc(50% - 12px)',
            color: (theme) => theme.palette.text.secondary,
          },
        },
        fieldset: (theme) => ({
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : warning
              ? `${theme.palette.warning.main} !important`
              : '',
        }),
        ...sx,
      }}
    >
      {optionsLoading && (
        <MenuItem disabled value="" sx={{ justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </MenuItem>
      )}
      {!optionsLoading &&
        options?.map((option, index) => (
          <MenuItem
            key={optionValue?.(option) ?? index}
            value={optionValue?.(option) ?? ''}
            onClick={() => onOptionSelect?.(option)}
            disabled={disabled}
            sx={{ fontSize: '0.875rem' }}
          >
            {optionLabel?.(option) ?? ''}
          </MenuItem>
        ))}
      {!optionsLoading && !options?.length && (
        <MenuItem disabled value="" sx={{ justifyContent: 'center', fontSize: '0.875rem' }}>
          {noOptionsText ?? t('global.noOptions')}
        </MenuItem>
      )}
      {placeholder && (
        <MenuItem disabled value="" sx={{ fontSize: '0.875rem', display: 'none' }}>
          {placeholder}
        </MenuItem>
      )}
    </Select>
  );
};
